/**
 * Login View
 *
 * Two exports:
 *   - <LoginView />         — full-page version with Card chrome, used as a fallback
 *                             page render (also kept for any deep-linked usage).
 *   - <LoginViewContent />  — inner form-only version (no Card, no page wrapper).
 *                             Used by LoginModal on the landing page so the form
 *                             can sit inside the modal's split-layout right panel.
 *
 * Both share the same auth/state logic; LoginViewContent contains the actual
 * form fields, tabs, promo banner, and provider buttons.
 */

import { useState, useEffect } from 'react';
import { Mail, Lock, LogIn, UserPlus, Loader2, Gift, ArrowLeft, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, CheckCircle } from 'lucide-react';

interface LoginViewProps {
  onForgotPassword?: () => void;
  onBackToLanding?: () => void;
  initialTab?: 'signin' | 'signup';
  onDatenschutzClick?: () => void;
  onImpressumClick?: () => void;
}

interface LoginViewContentProps {
  onForgotPassword?: () => void;
  initialTab?: 'signin' | 'signup';
  onDatenschutzClick?: () => void;
  onImpressumClick?: () => void;
  /**
   * When true, renders without the surrounding shadcn Card — used inside the
   * landing-page LoginModal where the modal panel itself provides the surface.
   */
  embedded?: boolean;
  /**
   * Called when an authentication action completes successfully and the parent
   * may want to react (e.g. close the modal). Optional — not all parents need it.
   */
  onAuthSuccess?: () => void;
}

/**
 * Inner form + auth logic. Renders the tabs, form fields, promo banner, OAuth
 * buttons, and footer links. Wrapped in a Card by default; pass `embedded` to
 * skip the Card chrome.
 */
export const LoginViewContent: React.FC<LoginViewContentProps> = ({
  onForgotPassword,
  initialTab = 'signin',
  onDatenschutzClick,
  onImpressumClick,
  embedded = false,
  onAuthSuccess,
}) => {
  const { signIn, signUp, signInWithProvider } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [isLoading, setIsLoading] = useState(false);
  const [providerLoading, setProviderLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState('');
  // Empfehlungscode is collapsed by default to save vertical space; expands on
  // click. When a referral was pre-filled (user came via a referral link), we
  // auto-expand so they see and can verify the value.
  const [referralExpanded, setReferralExpanded] = useState(false);

  // Pre-fill referral code from localStorage (set by URL parameters in main.tsx)
  useEffect(() => {
    const storedRef = localStorage.getItem('referrer_id');
    if (storedRef) {
      setReferralCode(storedRef);
      setReferralExpanded(true);
    }
  }, []);

  // Sync activeTab when initialTab prop changes (e.g. user clicks Login while
  // modal is mounted on signup tab).
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const isSignUp = activeTab === "signup";

  const handleProviderSignIn = async (provider: 'google') => {
    setError(null);
    setProviderLoading(provider);
    const { error } = await signInWithProvider(provider);
    if (error) {
      setError(error);
      setProviderLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      if (isSignUp) {
        const { error } = await signUp(email, password, referralCode || undefined);
        if (error) {
          setError(error);
        } else {
          setSuccessMessage('Registrierung erfolgreich! Bitte überprüfe deine E-Mail zur Bestätigung.');
          setActiveTab("signin");
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error);
        } else {
          // Successful sign-in — let parent close the modal if they care.
          onAuthSuccess?.();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Inner content (tabs + form + OAuth + footer)
  // ────────────────────────────────────────────────────────────────────────
  const inner = (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <CardHeader className="space-y-1 px-0 pb-4">
        <div className="flex items-center justify-between mb-2">
          <CardTitle className="text-2xl">
            {isSignUp ? 'Konto erstellen' : 'Willkommen zurück'}
          </CardTitle>
        </div>
        <CardDescription>
          {isSignUp
            ? 'Gib deine Daten ein, um ein neues Konto zu erstellen'
            : 'Melde dich an, um auf deine Projekte zuzugreifen'}
        </CardDescription>
        <TabsList className="grid w-full grid-cols-2 mt-4">
          <TabsTrigger value="signin">Anmelden</TabsTrigger>
          <TabsTrigger value="signup">Registrieren</TabsTrigger>
        </TabsList>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {/* Signup bonus banner — every new account starts with 10 free credits */}
        {isSignUp && (
          <Alert className="border-primary/30 bg-primary/5 mb-4">
            <Gift className="h-4 w-4 text-primary" />
            <AlertTitle className="text-primary font-semibold">10 Credits gratis</AlertTitle>
            <AlertDescription className="text-sm">
              Registriere dich jetzt und erhalte <span className="font-bold">10 Credits gratis</span> zum Ausprobieren.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Passwort</Label>
              {!isSignUp && onForgotPassword && (
                <Button
                  variant="link"
                  className="p-0 h-auto text-xs text-primary hover:text-primary/80"
                  onClick={onForgotPassword}
                  type="button"
                >
                  Passwort vergessen?
                </Button>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10"
                required
                minLength={6}
              />
            </div>
            {isSignUp && (
              <p className="text-xs text-muted-foreground">
                Mindestens 6 Zeichen
              </p>
            )}
          </div>

          {isSignUp && (
            <div className="space-y-2">
              {!referralExpanded ? (
                <button
                  type="button"
                  onClick={() => setReferralExpanded(true)}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                  Empfehlungscode hinzufügen <span className="opacity-60">(optional)</span>
                </button>
              ) : (
                <>
                  <Label htmlFor="referral-code">
                    Empfehlungscode <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <div className="relative">
                    <Gift className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="referral-code"
                      type="text"
                      placeholder="z.B. ABC12"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      maxLength={5}
                      className="pl-10 font-mono uppercase"
                      autoFocus
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Fehler</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {successMessage && (
            <Alert className="border-green-500/50 text-green-500 [&>svg]:text-green-500">
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>Erfolg</AlertTitle>
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : isSignUp ? (
              <UserPlus className="mr-2 h-4 w-4" />
            ) : (
              <LogIn className="mr-2 h-4 w-4" />
            )}
            {isSignUp ? 'Konto erstellen' : 'Anmelden'}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">oder</span>
          </div>
        </div>

        {/* Social Login Buttons */}
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => handleProviderSignIn('google')}
            disabled={providerLoading !== null}
          >
            {providerLoading === 'google' ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <GoogleIcon />
            )}
            <span className="ml-2">Mit Google fortfahren</span>
          </Button>
        </div>

        {/* Bottom inline switch — replaces the top Tabs on mobile.
            Hidden on desktop via CSS, visible on tablet/phone (≤760px). */}
        <div className="lp-login-modal-switch">
          {isSignUp ? (
            <>
              Du hast schon einen Account?{' '}
              <button type="button" onClick={() => setActiveTab('signin')}>
                Anmelden
              </button>
            </>
          ) : (
            <>
              Noch keinen Account?{' '}
              <button type="button" onClick={() => setActiveTab('signup')}>
                Registrieren
              </button>
            </>
          )}
        </div>
      </CardContent>
    </Tabs>
  );

  return (
    <>
      {embedded ? (
        // No surrounding card — modal panel provides its own surface
        <div className="w-full">{inner}</div>
      ) : (
        <Card className="shadow-lg">{inner}</Card>
      )}

      {(onDatenschutzClick || onImpressumClick) && (
        <div className="flex items-center justify-center gap-6 pt-4 text-xs text-muted-foreground">
          {onImpressumClick && (
            <button
              type="button"
              onClick={onImpressumClick}
              className="hover:text-foreground transition-colors"
            >
              Impressum
            </button>
          )}
          {onDatenschutzClick && (
            <button
              type="button"
              onClick={onDatenschutzClick}
              className="hover:text-foreground transition-colors"
            >
              Datenschutz
            </button>
          )}
        </div>
      )}
    </>
  );
};

/**
 * Full-page LoginView — kept for any direct deep-link usage outside the
 * landing-modal flow. The landing page itself now uses LoginModal.
 */
export const LoginView: React.FC<LoginViewProps> = ({
  onForgotPassword,
  onBackToLanding,
  initialTab = 'signin',
  onDatenschutzClick,
  onImpressumClick,
}) => {
  useEffect(() => {
    const prev = document.title;
    document.title = initialTab === 'signup' ? 'Registrieren — estage.ai' : 'Anmelden — estage.ai';
    return () => { document.title = prev; };
  }, [initialTab]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="relative w-full max-w-md space-y-8">
        {/* Back to Landing */}
        {onBackToLanding && (
          <button
            onClick={onBackToLanding}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Startseite
          </button>
        )}

        {/* Logo */}
        <div className="flex flex-col items-center justify-center gap-4">
          <img src="/logo.png" alt="estage.ai" className="w-12 h-12" />
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            estage<span className="text-primary">.ai</span>
          </h1>
          <p className="text-muted-foreground">Professionelles Virtual Staging für Immobilien</p>
        </div>

        <LoginViewContent
          onForgotPassword={onForgotPassword}
          initialTab={initialTab}
          onDatenschutzClick={onDatenschutzClick}
          onImpressumClick={onImpressumClick}
        />
      </div>
    </div>
  );
};
